import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { clearDeliveryNoteRowCache } from '~/components/deliveries/utils';

import { queryKeysDeliveryNote } from '../queryKeys';

import { bulkBillDlns } from './bulkBillDlns';
import { type BulkBillDlnsPayload, type BulkBillDlnsResponse } from './types';

/**
 * React Query mutation hook for bulk-billing delivery notes.
 *
 * @param options - Optional TanStack Query mutation options for customizing behavior.
 * @returns A mutation object with methods to trigger the bulk billing.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/bulk_bill_dlns_delivery_note_bulk_bill_put
 */
export const useMutationBulkBillDlns = (
  options?: Omit<
    UseMutationOptions<BulkBillDlnsResponse, Error, BulkBillDlnsPayload>,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();
  const { onSettled, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: (payload: BulkBillDlnsPayload) => bulkBillDlns(payload),
    onSettled: (data, error, variables, onMutateResult, context) => {
      if (variables?.assetIds) {
        for (const assetId of variables.assetIds) {
          void queryClient.invalidateQueries({
            queryKey: queryKeysDeliveryNote.getInternal(assetId),
          });
          void queryClient.invalidateQueries({
            queryKey: queryKeysDeliveryNote.get(assetId),
          });
        }
      }

      clearDeliveryNoteRowCache();
      void queryClient.invalidateQueries({
        queryKey: [queryKeysDeliveryNote.base(), 'list'],
        refetchType: 'all',
      });

      void onSettled?.(data, error, variables, onMutateResult, context);
    },
    ...restOptions,
  });
};
